import { z } from 'zod';
import { CUSTOMER_ORIGINS } from '../constants/customer-origins.js';
import { IMPORT_MAX_ROWS_PER_REQUEST } from '../lib/import-chunking.js';
import { emailSchema, importResultSchema, importRowSchema, patchableSchema, usPhoneSchema, uuidSchema, } from './common.js';
export const addressSchema = z.object({
    line1: z.string().trim().min(1).max(200),
    line2: z.string().trim().max(200).nullable().default(null),
    city: z.string().trim().min(1).max(100),
    state: z.string().trim().length(2, 'Two-letter state code').toUpperCase(),
    postalCode: z
        .string()
        .trim()
        .regex(/^\d{5}(-\d{4})?$/, 'ZIP must be 12345 or 12345-6789'),
    country: z.literal('US').default('US'),
});
export const createCustomerSchema = z.object({
    // Phone is now optional profile data — email + password is how everyone
    // signs in. Many customers still only give a phone number at the counter.
    phone: usPhoneSchema.nullable().default(null),
    name: z.string().trim().min(1).max(150),
    /** A business name or any other distinguishing identifier — e.g. a person
     *  with two accounts, or one held under another entity (t0853). Searched
     *  alongside name/phone/email, and shown next to the name so similar
     *  accounts can be told apart. */
    businessName: z.string().trim().max(150).nullable().default(null),
    email: emailSchema.nullable().default(null),
    address: addressSchema.nullable().default(null),
    notes: z.string().trim().max(2000).nullable().default(null),
    /** No sales tax is charged on this customer's orders (t0947). */
    taxExempt: z.boolean().default(false),
    /** Where the record came from beyond `origin` — "Google Contacts 2026-10",
     *  "Square export" (t1247). Free text. */
    source: z.string().trim().max(200).nullable().default(null),
    /** The record's id in another system, e.g. a Square customer id (t1247). */
    externalId: z.string().trim().max(200).nullable().default(null),
});
/** Patchable, not partial — see {@link patchableSchema}. Editing a name must
 *  not blank the email, address and notes the caller never sent. */
export const updateCustomerSchema = patchableSchema(createCustomerSchema.omit({ notes: true })).extend({
    isActive: z.boolean().optional(),
    phoneSmsCapable: z.boolean().optional(),
});
export const customerSchema = z.object({
    id: uuidSchema,
    phone: z.string().nullable(),
    name: z.string(),
    businessName: z.string().nullable(),
    email: z.string().nullable(),
    address: addressSchema.nullable(),
    notes: z.string().nullable(),
    isActive: z.boolean(),
    stripeCustomerId: z.string().nullable(),
    /** Whether the primary phone above can receive texts — meaningless while
     *  `phone` is null, but always present. */
    phoneSmsCapable: z.boolean(),
    /** How this customer record came to exist. Null on records that predate
     *  this field. */
    origin: z.enum(CUSTOMER_ORIGINS).nullable(),
    /** No sales tax is charged on this customer's orders. */
    taxExempt: z.boolean(),
    /** Free-text source of the record, e.g. "Square export" (t1247). */
    source: z.string().nullable(),
    /** Id of this customer in another system, e.g. Square (t1247). */
    externalId: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
export const customerListItemSchema = customerSchema.extend({
    orderCount: z.number().int().nonnegative(),
    balanceDueCents: z.number().int(),
    /** Money actually received net of refunds — not a sum of order totals,
     *  which would overcount a partially refunded order (t0856). */
    totalSpentCents: z.number().int().nonnegative(),
    /** Most recent sales order OR rental (any status) for this customer, by
     *  created-at — null only when they have neither (t0949). */
    lastActivityAt: z.iso.datetime().nullable(),
    /** Whether another customer record matches this one closely enough to be a
     *  possible duplicate (t0913) — every detail identical, or two or more of
     *  name/business name/email/phone/address. Surfaces a review badge; the
     *  actual grouping/merge flow lives on `GET /customers/duplicates`. */
    hasPotentialDuplicate: z.boolean(),
    /** Another customer has the same or a similar name — the lower-confidence
     *  review tier (t1247). */
    hasSimilarName: z.boolean(),
    /** Whether this customer can receive a text on ANY number — the primary or
     *  an extra (t0914) — unlike `phoneSmsCapable` above, which only describes
     *  the primary and is meaningless while `phone` is null. Drives bulk-text
     *  eligibility on the Customers list without a second round trip. */
    smsReachable: z.boolean(),
});
/**
 * A customer's extra phone numbers, beyond the primary on `customer.phone`
 * (t0835) — fetched separately, the same way `/customers/:id/devices` is,
 * rather than joined onto every list row.
 */
export const customerPhoneSchema = z.object({
    id: uuidSchema,
    phone: z.string(),
    label: z.string().nullable(),
    smsCapable: z.boolean(),
    createdAt: z.iso.datetime(),
});
export const addCustomerPhoneSchema = z.object({
    phone: usPhoneSchema,
    label: z.string().trim().min(1).max(50).nullable().default(null),
    smsCapable: z.boolean().default(false),
});
/** Label and SMS flag — the number itself is immutable once added; a wrong
 *  number is removed and re-added, not edited in place. */
export const updateCustomerPhoneSchema = z.object({
    label: z.string().trim().min(1).max(50).nullable(),
    smsCapable: z.boolean(),
});
/**
 * A card on file with the payment provider for this customer (t0855) — the
 * provider reference and the display fields the counter needs, never raw
 * card data. `expMonth`/`expYear` are a snapshot from when the card was
 * saved and can go stale — display-only.
 */
export const customerPaymentMethodSchema = z.object({
    id: uuidSchema,
    brand: z.string(),
    last4: z.string(),
    expMonth: z.number().int().min(1).max(12),
    expYear: z.number().int(),
    consentAt: z.iso.datetime(),
    createdAt: z.iso.datetime(),
});
/** A card staff can charge for a customer (t1117): one of their own
 *  (`owner: null`) or, when the list is asked to include linked customers, a
 *  family member's — `owner` names whose card it is. The card never moves
 *  between records; it is only offered. */
export const chargeableCardSchema = customerPaymentMethodSchema.extend({
    owner: z.object({ id: uuidSchema, name: z.string() }).nullable(),
});
/** A family/payer link between two separate customer records (t1117). */
export const createCustomerLinkSchema = z.object({
    /** The other customer to link to. */
    customerId: uuidSchema,
    /** Free text — "Parent", "Spouse", "Family". */
    label: z.string().trim().min(1).max(50).nullable().default(null),
    /** The consent record: who agreed the other customer's card may be used. */
    note: z.string().trim().min(1).max(500).nullable().default(null),
});
export const customerLinkSchema = z.object({
    id: uuidSchema,
    /** Always the OTHER customer, from the viewpoint of the profile asked for. */
    customer: z.object({
        id: uuidSchema,
        name: z.string(),
        businessName: z.string().nullable(),
        phone: z.string().nullable(),
        isActive: z.boolean(),
    }),
    label: z.string().nullable(),
    note: z.string().nullable(),
    createdByName: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
/** A staff-only note on a customer (t1251). */
export const createCustomerNoteSchema = z.object({
    body: z.string().trim().min(1).max(2000),
    /** Shown at the top of the customer page. */
    pinned: z.boolean().default(false),
    /** Shown in red. Independent of `pinned`. */
    important: z.boolean().default(false),
});
export const updateCustomerNoteSchema = patchableSchema(createCustomerNoteSchema);
export const customerNoteSchema = z.object({
    id: uuidSchema,
    body: z.string(),
    pinned: z.boolean(),
    important: z.boolean(),
    createdByName: z.string().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
/** What the admin dialog gets back after creating a SetupIntent — enough to
 *  mount Stripe.js and render the Payment Element for this one card save. */
export const paymentMethodSetupSchema = z.object({
    clientSecret: z.string(),
    publishableKey: z.string(),
});
/** `consent: z.literal(true)` rather than a plain boolean — a request that
 *  omits consent, or sends it false, cannot even parse, let alone store a
 *  card (t0855). */
export const attachCustomerPaymentMethodSchema = z.object({
    setupIntentId: z.string().trim().min(1).max(255),
    consent: z.literal(true),
});
/** Bulk import from the admin Customers screen's CSV dialog — see
 *  `importProductsSchema` for the shape and why the row schema stays loose. */
export const importCustomersSchema = z.object({
    updateExisting: z.boolean().default(false),
    rows: z.array(importRowSchema).min(1).max(IMPORT_MAX_ROWS_PER_REQUEST),
});
export const importCustomersResultSchema = importResultSchema();
const coercedBoolean = z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
    .optional();
export const customerListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
    q: z.string().trim().max(200).optional(),
    isActive: coercedBoolean,
    /** true = no phone anywhere on file (primary or extras); false = has one. */
    missingPhone: coercedBoolean,
    /** true = no email on file; false = has one. */
    missingEmail: coercedBoolean,
    /** true = some number on file (primary or extra) is SMS-capable. */
    smsCapable: coercedBoolean,
    /** 'unknown' matches records with no origin recorded (pre-t0852). */
    origin: z.enum([...CUSTOMER_ORIGINS, 'unknown']).optional(),
    /** true = only records flagged as a possible duplicate of another (t0913). */
    flaggedDuplicate: coercedBoolean,
    /** 'name' = only records with another customer of the same or a similar
     *  name — the lower-confidence review tier (t1247). */
    duplicateTier: z.enum(['strong', 'name']).optional(),
    /** Exact match on the free-text source (t1247). */
    source: z.string().trim().max(200).optional(),
    /** true = has an external id on file; false = has none (t1247). */
    hasExternalId: coercedBoolean,
    sort: z
        .enum([
        'name',
        'createdAt',
        'lastActivityAt',
        'phone',
        'email',
        'orderCount',
        'balanceDueCents',
        'totalSpentCents',
    ])
        .default('createdAt'),
    order: z.enum(['asc', 'desc']).default('desc'),
});
/**
 * Query params the customers CSV export accepts (t0854) — everything the
 * list's filter bar can set, minus `page`/`pageSize`: export is always the
 * full filtered set, never one page of it.
 */
export const customerExportQuerySchema = customerListQuerySchema.pick({
    q: true,
    isActive: true,
    missingPhone: true,
    missingEmail: true,
    smsCapable: true,
    origin: true,
    source: true,
    hasExternalId: true,
    sort: true,
    order: true,
});
// --- Deduplication and cleanup (t0913) -------------------------------------
/** The "customer details" columns two records can match on for the purposes
 *  of flagging or grouping them as possible duplicates. */
export const duplicateMatchFieldSchema = z.enum([
    'name',
    'businessName',
    'email',
    'phone',
    'address',
]);
/** A customer plus the linked-activity counts a merge dialog needs to show
 *  what will move, and whether it is even safe to pick as the record that
 *  goes away. */
export const customerDuplicateRecordSchema = customerSchema.extend({
    orderCount: z.number().int().nonnegative(),
    repairTicketCount: z.number().int().nonnegative(),
    supportTicketCount: z.number().int().nonnegative(),
    subscriptionCount: z.number().int().nonnegative(),
    extraPhoneCount: z.number().int().nonnegative(),
    paymentMethodCount: z.number().int().nonnegative(),
    /** Whether this record has a password set — i.e. can sign in on its own. */
    hasCredentials: z.boolean(),
});
/** Optional search for the duplicates report (t1345): narrows every list to
 *  pairs involving a customer matching this name, email or phone. */
export const customerDuplicatesQuerySchema = z.object({
    q: z.string().trim().max(200).optional(),
});
export const customerDuplicatesSchema = z.object({
    /** Records where every stored detail is identical. First record in each
     *  group is the one auto-dedup keeps. */
    exactGroups: z.array(z.object({ records: z.array(customerDuplicateRecordSchema).min(2) })),
    /** Pairs matching on two or more details — reviewed and merged by hand.
     *  Capped; see `partialPairsTotal` for the true count. */
    partialPairs: z.array(z.object({
        a: customerDuplicateRecordSchema,
        b: customerDuplicateRecordSchema,
        matchedFields: z.array(duplicateMatchFieldSchema).min(2),
    })),
    partialPairsTotal: z.number().int().nonnegative(),
    /** Pairs that share only a name, or have a similar one (case, spacing,
     *  typos) — the lowest-confidence tier, always reviewed by hand (t1247).
     *  Pairs already listed above are left out. Capped; see `namePairsTotal`. */
    namePairs: z.array(z.object({
        a: customerDuplicateRecordSchema,
        b: customerDuplicateRecordSchema,
        matchKind: z.enum(['same_name', 'similar_name']),
        /** 0–1 trigram similarity of the two names. */
        similarity: z.number(),
    })),
    /** Total of the two below. The list is capped per kind, so a long run of
     *  same-name pairs cannot crowd the similar-name ones out (t1345). */
    namePairsTotal: z.number().int().nonnegative(),
    sameNamePairsTotal: z.number().int().nonnegative(),
    similarNamePairsTotal: z.number().int().nonnegative(),
});
/** One page of a single duplicate list (t1346), so every pair is reachable by
 *  scrolling instead of being cut off at a fixed cap. */
export const customerDuplicatePairsQuerySchema = z.object({
    kind: z.enum(['partial', 'same_name', 'similar_name']),
    q: z.string().trim().max(200).optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export const customerDuplicatePairsPageSchema = z.object({
    items: z.array(z.object({
        a: customerDuplicateRecordSchema,
        b: customerDuplicateRecordSchema,
        /** Details the two matched on — only for `partial`. */
        matchedFields: z.array(duplicateMatchFieldSchema),
        /** 0–1 trigram similarity of the names — only for the name kinds. */
        similarity: z.number().nullable(),
    })),
    total: z.number().int().nonnegative(),
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
});
/** Two admin-picked customers, hydrated like duplicate-report records so the
 *  merge dialog can preview them (t1118). */
export const mergeRecordsQuerySchema = z
    .object({ a: uuidSchema, b: uuidSchema })
    .refine((d) => d.a !== d.b, { message: 'Pick two different customers' });
export const mergeRecordsSchema = z.object({
    a: customerDuplicateRecordSchema,
    b: customerDuplicateRecordSchema,
});
/** Everyone who looks like a duplicate of one customer, for the inline Merge
 *  on the Customers list (t1344). Best match first. */
export const customerDuplicateMatchesSchema = z.object({
    customer: customerDuplicateRecordSchema,
    matches: z.array(z.object({
        record: customerDuplicateRecordSchema,
        matchKind: z.enum(['exact', 'details', 'same_name', 'similar_name']),
        /** Which details matched — only for `details`. */
        matchedFields: z.array(duplicateMatchFieldSchema),
        /** 0–1 trigram similarity of the names — only for the name kinds. */
        similarity: z.number().nullable(),
    })),
});
export const mergeCustomersSchema = z
    .object({ winnerId: uuidSchema, loserId: uuidSchema })
    .refine((d) => d.winnerId !== d.loserId, { message: 'Pick two different customers' });
export const mergeSummarySchema = z.object({
    orders: z.number().int().nonnegative(),
    repairTickets: z.number().int().nonnegative(),
    supportTickets: z.number().int().nonnegative(),
    subscriptions: z.number().int().nonnegative(),
    phones: z.number().int().nonnegative(),
    paymentMethods: z.number().int().nonnegative(),
    /** Family links carried over to the winner (t1117) — links that would have
     *  linked the winner to itself, or duplicated an existing one, are dropped
     *  and not counted. */
    links: z.number().int().nonnegative(),
    /** The losing record's email, when both records had one and the winner's
     *  was kept instead — nothing is deleted silently, but it is dropped from
     *  the merged record and only recoverable from the audit log. */
    droppedEmail: z.string().nullable(),
});
export const mergeCustomersResultSchema = z.object({
    customer: customerSchema,
    moved: mergeSummarySchema,
});
export const deduplicateCustomersResultSchema = z.object({
    merged: z.array(z.object({ winnerId: uuidSchema, mergedIds: z.array(uuidSchema).min(1) })),
    failed: z.array(z.object({ ids: z.array(uuidSchema), reason: z.string() })),
});
export const cleanupCandidateSchema = customerSchema.pick({
    id: true,
    name: true,
    businessName: true,
    address: true,
    notes: true,
    origin: true,
    isActive: true,
    phone: true,
    email: true,
    source: true,
    externalId: true,
    createdAt: true,
});
/**
 * Which records the bulk clean-up lists (t1247). `unused` is the original
 * rule: no phone, no email, and no linked activity. `no_activity` drops the
 * phone/email requirement — junk phone-book entries that have a number but
 * were never used for a sale, support request, card, subscription or credit.
 */
export const cleanupScopeSchema = z.enum(['unused', 'no_activity']);
export const cleanupFilterSchema = z.object({
    q: z.string().trim().max(200).optional(),
    scope: cleanupScopeSchema.default('unused'),
    /** true = only records with no email; false = only records that have one. */
    missingEmail: coercedBoolean,
    /** true = only records with no phone anywhere; false = only those with one. */
    missingPhone: coercedBoolean,
    origin: z.enum([...CUSTOMER_ORIGINS, 'unknown']).optional(),
    source: z.string().trim().max(200).optional(),
    /** Only records created before this date (YYYY-MM-DD). */
    createdBefore: z.iso.date().optional(),
});
export const cleanupCandidatesQuerySchema = cleanupFilterSchema.extend({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
/** Every id matching the clean-up filters, for "select all N matching". */
export const cleanupCandidateIdsSchema = z.object({
    ids: z.array(uuidSchema),
    total: z.number().int().nonnegative(),
});
/** The most ids "select all matching" will return in one go. */
export const CLEANUP_IDS_CAP = 5000;
export const bulkDeleteCustomersSchema = z.object({
    ids: z.array(uuidSchema).min(1).max(500),
    /** Each id is re-checked against this rule at delete time. */
    scope: cleanupScopeSchema.default('unused'),
});
export const bulkDeleteCustomersResultSchema = z.object({
    deleted: z.array(uuidSchema),
    failed: z.array(z.object({ id: uuidSchema, reason: z.string() })),
});
// --- Bulk actions from the Customers list selection (t0914) ----------------
/** One text, composed once, sent as a separate individual SMS to each
 *  selected customer — never a group text. Capped at 200, matching
 *  `bulkTransitionUnitsSchema`. */
export const bulkMessageCustomersSchema = z.object({
    customerIds: z.array(uuidSchema).min(1).max(200),
    body: z.string().trim().min(1).max(640),
});
const bulkCustomerOutcomeSchema = z.object({
    customerId: uuidSchema,
    /** Null when the record no longer exists — deleted between the list
     *  loading and the admin sending. */
    name: z.string().nullable(),
    reason: z.string(),
});
export const bulkMessageCustomersResultSchema = z.object({
    sent: z.array(uuidSchema),
    /** Ineligible before a send was ever attempted — inactive, or no
     *  text-capable number on file. */
    skipped: z.array(bulkCustomerOutcomeSchema),
    /** Eligible, but the provider rejected the send (or the record vanished). */
    failed: z.array(bulkCustomerOutcomeSchema),
});
export const bulkActiveCustomersSchema = z.object({
    customerIds: z.array(uuidSchema).min(1).max(200),
    isActive: z.boolean(),
});
export const bulkActiveCustomersResultSchema = z.object({
    updated: z.array(uuidSchema),
    failed: z.array(z.object({ customerId: uuidSchema, reason: z.string() })),
});
// --- Merge history and undo (t1247) -----------------------------------------
export const customerMergeSchema = z.object({
    id: uuidSchema,
    /** Null if the kept record was deleted afterwards. */
    winnerId: uuidSchema.nullable(),
    loserId: uuidSchema,
    winnerName: z.string(),
    loserName: z.string(),
    mergedByName: z.string().nullable(),
    /** How much history moved onto the kept record. */
    moved: z.object({
        orders: z.number().int().nonnegative(),
        repairTickets: z.number().int().nonnegative(),
        supportTickets: z.number().int().nonnegative(),
        subscriptions: z.number().int().nonnegative(),
        phones: z.number().int().nonnegative(),
        paymentMethods: z.number().int().nonnegative(),
        notes: z.number().int().nonnegative(),
    }),
    createdAt: z.iso.datetime(),
    undoneAt: z.iso.datetime().nullable(),
});
export const customerMergesQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export const undoMergeResultSchema = z.object({
    /** The record that was brought back. */
    restored: customerSchema,
    /** The record that had absorbed it. */
    winner: customerSchema,
});
//# sourceMappingURL=customer.js.map